// Blocked periods for time-off requests ("no leave over Christmas / New Year").
//
// The database is the real gate (a driver's request that falls in a block that covers them is
// refused with the admin's message -- migration 0125); the pure rules here mirror it so the
// request form can say so up front, and the admin screen can count what a block affects.
import { supabase } from "@/lib/supabase";

export interface TimeOffBlock {
  id: string;
  name: string;
  /** ISO date (YYYY-MM-DD), inclusive. One day = same start and end. */
  start_date: string;
  end_date: string;
  /** Optional daily window "HH:MM"; an end at or before the start runs overnight (e.g. 17:00-03:00). */
  start_time: string | null;
  end_time: string | null;
  /** Roster groups (teams) it covers. Empty AND no driver types = everyone. */
  team_names: string[];
  driver_type_ids: string[];
  /** What the driver is told when they try to request leave inside it. */
  message: string;
  is_active: boolean;
}

export interface BlockDriver {
  groups: string[];
  driverTypeId: string | null;
}

export interface BlockRequest {
  type: "partial_day" | "full_day" | "multi_day";
  startDate: string;
  endDate: string;
  startTime?: string | null;
  endTime?: string | null;
}

export const DEFAULT_BLOCK_MESSAGE =
  "Leave can't be requested for these dates because we need full cover. Please speak to the office if it's urgent.";

const day = (d: string) => d.slice(0, 10);

export const hhmmToMinutes = (t: string | null | undefined): number | null => {
  if (!t) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(t);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

const segments = (start: number, end: number): [number, number][] =>
  end > start
    ? [[start, end]]
    : [
        [start, 1440],
        [0, end],
      ];

/** Do two daily time windows overlap? A window ending at or before its start runs overnight. */
export function timeWindowsOverlap(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
  for (const [as, ae] of segments(aStart, aEnd)) {
    for (const [bs, be] of segments(bStart, bEnd)) {
      if (as < be && bs < ae) return true;
    }
  }
  return false;
}

export const blockIsEveryone = (b: Pick<TimeOffBlock, "team_names" | "driver_type_ids">) =>
  b.team_names.length === 0 && b.driver_type_ids.length === 0;

/** A block covers a driver if it is for everyone, or the driver is in any listed team or of any listed type. */
export function blockCoversDriver(b: Pick<TimeOffBlock, "team_names" | "driver_type_ids">, driver: BlockDriver): boolean {
  if (blockIsEveryone(b)) return true;
  if (driver.groups.some((g) => b.team_names.includes(g))) return true;
  return !!driver.driverTypeId && b.driver_type_ids.includes(driver.driverTypeId);
}

/** Do the block's dates (and, for an hours request, its daily window) overlap the request? */
export function blockOverlapsRequest(b: TimeOffBlock, req: BlockRequest): boolean {
  if (!(day(b.start_date) <= day(req.endDate) && day(b.end_date) >= day(req.startDate))) return false;
  const bs = hhmmToMinutes(b.start_time);
  const be = hhmmToMinutes(b.end_time);
  const rs = hhmmToMinutes(req.startTime);
  const re = hhmmToMinutes(req.endTime);
  if (bs !== null && be !== null && req.type === "partial_day" && rs !== null && re !== null) {
    return timeWindowsOverlap(rs, re, bs, be);
  }
  return true;
}

/** Active blocks that cover this driver and stop this request, earliest first. */
export function blocksStoppingRequest(blocks: TimeOffBlock[], driver: BlockDriver, req: BlockRequest): TimeOffBlock[] {
  return blocks
    .filter((b) => b.is_active && blockCoversDriver(b, driver) && blockOverlapsRequest(b, req))
    .sort((a, b) => day(a.start_date).localeCompare(day(b.start_date)));
}

export const formatBlockDay = (iso: string) => {
  const [y, m, d] = day(iso).split("-");
  return `${d}/${m}/${y}`;
};

export function formatBlockDates(b: Pick<TimeOffBlock, "start_date" | "end_date" | "start_time" | "end_time">): string {
  const dates =
    day(b.start_date) === day(b.end_date)
      ? formatBlockDay(b.start_date)
      : `${formatBlockDay(b.start_date)} – ${formatBlockDay(b.end_date)}`;
  return b.start_time && b.end_time ? `${dates}, ${b.start_time.slice(0, 5)}–${b.end_time.slice(0, 5)} each day` : dates;
}

export function describeBlockAudience(
  b: Pick<TimeOffBlock, "team_names" | "driver_type_ids">,
  typeNames: Record<string, string>
): string {
  if (blockIsEveryone(b)) return "Everyone";
  return [...b.team_names, ...b.driver_type_ids.map((id) => typeNames[id] ?? "Unknown type")].join(", ");
}

// ---------------------------------------------------------------------------
// Database
// ---------------------------------------------------------------------------

const BLOCK_COLUMNS = "id, name, start_date, end_date, start_time, end_time, team_names, driver_type_ids, message, is_active";

export async function loadTimeOffBlocks(): Promise<TimeOffBlock[]> {
  const { data, error } = await supabase
    .from("time_off_blocked_periods")
    .select(BLOCK_COLUMNS)
    .order("start_date", { ascending: true });
  if (error) {
    console.error("loadTimeOffBlocks error:", error);
    return [];
  }
  return (data as TimeOffBlock[]) || [];
}

export async function saveTimeOffBlock(block: Omit<TimeOffBlock, "id"> & { id?: string }): Promise<void> {
  const payload = {
    name: block.name.trim(),
    start_date: block.start_date,
    end_date: block.end_date,
    start_time: block.start_time || null,
    end_time: block.end_time || null,
    team_names: block.team_names,
    driver_type_ids: block.driver_type_ids,
    message: block.message.trim(),
    is_active: block.is_active,
  };
  const { error } = block.id
    ? await supabase.from("time_off_blocked_periods").update(payload).eq("id", block.id)
    : await supabase.from("time_off_blocked_periods").insert(payload);
  if (error) throw error;
}

export async function deleteTimeOffBlock(id: string): Promise<void> {
  const { error } = await supabase.from("time_off_blocked_periods").delete().eq("id", id);
  if (error) throw error;
}

/** The team(s) and driver type a driver belongs to, for working out which blocks cover them. */
export async function loadBlockDriver(driverName: string): Promise<BlockDriver> {
  const { data } = await supabase
    .from("drivers")
    .select("roster_groups, driver_type_id")
    .eq("driver_name", driverName)
    .maybeSingle();
  return {
    groups: ((data as any)?.roster_groups as string[] | null) ?? [],
    driverTypeId: ((data as any)?.driver_type_id as string | null) ?? null,
  };
}
